import { ajaxCall } from "@/shared/connection/ajax-call";
import { handleErrorByMessage } from "@/shared/connection/with-store-error-handlers";
import { t } from "@/i18n";
import { computed, markRaw } from "vue";
import {
    ShoppingCartIcon,
    ShoppingBasketIcon,
    ListIcon,
    AddCircleIcon,
    ArrowReloadIcon,
    UserCircleSingleIcon,
    UsersIcon,
    CheckIcon,
    InformationCircleIcon,
    RocketIcon,
    ShippingBoxIcon,
    AddIcon,
    AddBookmarkIcon,
    Building2Icon,
    BusinessHandshakeIcon,
    CalendarIcon,
    CheckCircleIcon,
    CheckSquareIcon,
    CoinsHandIcon,
    ConvertPdfIcon,
    CurrencyDolarCircleIcon,
    DolarCoinIcon,
    DownloadFileIcon,
    GlobeIcon,
    GraphBarIncreaseIcon,
    HeartCircleIcon,
    LineChartUpIcon,
    LoveIcon,
    StoreIcon,
    TransactionRepeatIcon,
    WalletIcon,
} from "@easy/ui/icons";

const TICKER_ENDPOINT = `/tickers`;

export const tickersService = {
    publish({ uuid, code, publish }) {
        return ajaxCall
            .post(`${TICKER_ENDPOINT}/${uuid}/code`, { code, publish })
            .catch(handleErrorByMessage());
    },
    getExampleData() {
        return computed(() => ({
            product_name: t("ticker.examples.product_name"),
            price: t("ticker.examples.price"),
            customer_name: t("ticker.examples.customer_name"),
            city: t("ticker.examples.city"),
            country_code: t("ticker.examples.country_code"),
        }));
    },
    getEventTypes() {
        const exampleData = this.getExampleData().value;
        // fix en / pl language change with computed()
        return computed(() => [
            {
                code: "PRODUCT_PURCHASED",
                title: t("ticker.events.product_purchased.title"),
                description: t("ticker.events.product_purchased.description", {
                    product_name: "{product_name}",
                    price: "{price}",
                }),
                example: t("ticker.events.product_purchased.description", exampleData),
                icon: "ShoppingCartIcon",
                aggregate: t("ticker.events.product_purchased.aggregate", {
                    count: "{count}",
                    product_name: "{product_name}",
                }),
            },
            {
                code: "SUBSCRIPTION_CREATED",
                title: t("ticker.events.subscription_created.title"),
                description: t("ticker.events.subscription_created.description", {
                    product_name: "{product_name}",
                }),
                example: t("ticker.events.subscription_created.description", exampleData),
                icon: "WalletIcon",
                aggregate: t("ticker.events.subscription_created.aggregate", {
                    count: "{count}",
                    product_name: "{product_name}",
                }),
            },
            {
                code: "SUBSCRIPTION_RENEWED",
                title: t("ticker.events.subscription_renewed.title"),
                description: t("ticker.events.subscription_renewed.description", {
                    product_name: "{product_name}",
                }),
                example: t("ticker.events.subscription_renewed.description", exampleData),
                icon: "ArrowReloadIcon",
                aggregate: t("ticker.events.subscription_renewed.aggregate", {
                    count: "{count}",
                    product_name: "{product_name}",
                }),
            },
            {
                code: "WAITLIST_SUBSCRIBED",
                title: t("ticker.events.waitlist_subscribed.title"),
                description: t("ticker.events.waitlist_subscribed.description", {
                    product_name: "{product_name}",
                }),
                example: t("ticker.events.waitlist_subscribed.description", exampleData),
                icon: "ListIcon",
                aggregate: t("ticker.events.waitlist_subscribed.aggregate", {
                    count: "{count}",
                    product_name: "{product_name}",
                }),
            },
            {
                code: "CHECKOUT_SESSION_CREATED",
                title: t("ticker.events.checkout_session_created.title"),
                description: t("ticker.events.checkout_session_created.description", {
                    product_name: "{product_name}",
                }),
                example: t("ticker.events.checkout_session_created.description", exampleData),
                icon: "ShoppingBasketIcon",
                aggregate: t("ticker.events.checkout_session_created.aggregate", {
                    count: "{count}",
                    product_name: "{product_name}",
                }),
            },
        ]);
    },
    getIconsList() {
        return [
            { name: "ShoppingCartIcon", icon: markRaw(ShoppingCartIcon) },
            { name: "ShoppingBasketIcon", icon: markRaw(ShoppingBasketIcon) },
            { name: "ListIcon", icon: markRaw(ListIcon) },
            { name: "AddCircleIcon", icon: markRaw(AddCircleIcon) },
            { name: "ArrowReloadIcon", icon: markRaw(ArrowReloadIcon) },
            { name: "UserCircleSingleIcon", icon: markRaw(UserCircleSingleIcon) },
            { name: "UsersIcon", icon: markRaw(UsersIcon) },
            { name: "CheckIcon", icon: markRaw(CheckIcon) },
            { name: "InformationCircleIcon", icon: markRaw(InformationCircleIcon) },
            { name: "RocketIcon", icon: markRaw(RocketIcon) },
            { name: "ShippingBoxIcon", icon: markRaw(ShippingBoxIcon) },
            { name: "AddIcon", icon: markRaw(AddIcon) },
            { name: "AddBookmarkIcon", icon: markRaw(AddBookmarkIcon) },
            { name: "Building2Icon", icon: markRaw(Building2Icon) },
            { name: "BusinessHandshakeIcon", icon: markRaw(BusinessHandshakeIcon) },
            { name: "CalendarIcon", icon: markRaw(CalendarIcon) },
            { name: "CheckCircleIcon", icon: markRaw(CheckCircleIcon) },
            { name: "CheckSquareIcon", icon: markRaw(CheckSquareIcon) },
            { name: "CoinsHandIcon", icon: markRaw(CoinsHandIcon) },
            { name: "ConvertPdfIcon", icon: markRaw(ConvertPdfIcon) },
            { name: "CurrencyDolarCircleIcon", icon: markRaw(CurrencyDolarCircleIcon) },
            { name: "DolarCoinIcon", icon: markRaw(DolarCoinIcon) },
            { name: "DownloadFileIcon", icon: markRaw(DownloadFileIcon) },
            { name: "GlobeIcon", icon: markRaw(GlobeIcon) },
            { name: "GraphBarIncreaseIcon", icon: markRaw(GraphBarIncreaseIcon) },
            { name: "HeartCircleIcon", icon: markRaw(HeartCircleIcon) },
            { name: "LineChartUpIcon", icon: markRaw(LineChartUpIcon) },
            { name: "LoveIcon", icon: markRaw(LoveIcon) },
            { name: "StoreIcon", icon: markRaw(StoreIcon) },
            { name: "TransactionRepeatIcon", icon: markRaw(TransactionRepeatIcon) },
            { name: "WalletIcon", icon: markRaw(WalletIcon) },
        ];
    },
};
